import { Activity, Calendar, Users } from 'lucide-react';
import { NavChart } from '../../../assets/icons/nav-chart.tsx';
import { NavCustomer } from '../../../assets/icons/nav-customer.tsx';
import { NavHome } from '../../../assets/icons/nav-home.tsx';
import { NavTask } from '../../../assets/icons/nav-task.tsx';
import { OrderIcon } from '../../../assets/icons/order-icon.tsx';
import { QuoteIcon } from '../../../assets/icons/quote-icon.tsx';
import type { MenuGroup, MenuItem } from '../contracts.ts';
import { NavPathOverrideEnum } from '../contracts.ts';
import type { BuildNavigationOptions } from './build-navigation-types.ts';

/**
 * The grouped manager cockpit menu: an unlabelled launchpad (Home, Tasks),
 * then the labelled Steer (Team, Customers) and Analyze (Sales Reports,
 * Orders, Quotes, Activities, Events) groups.
 *
 * Unlike the rep branch there is NO single-subitem collapse: Steer and
 * Analyze always render as groups even when gating trims them to one child —
 * grouping is the whole point of the manager view. Empty groups are dropped.
 */
export const buildManagerNavigation = (options: BuildNavigationOptions): MenuGroup[] => {
    const { t, taskCount, appConfig } = options;
    const {
        canReadOrders,
        canReadQuotes,
        canViewTeamPage,
        canViewGlobalAnalytics,
        enableCustomerEventPlanning,
    } = appConfig;

    const rawGroups: { id: string; label?: string; items: (MenuItem | false)[] }[] = [
        {
            id: 'launchpad',
            items: [
                {
                    id: NavPathOverrideEnum.HOME,
                    name: t('page_title__home'),
                    href: '/home',
                    icon: NavHome,
                },
                {
                    id: NavPathOverrideEnum.TASKS,
                    name: t('page_title__tasks'),
                    href: '/tasks',
                    icon: NavTask,
                    count: taskCount,
                },
            ],
        },
        {
            id: 'steer',
            label: t('nav_group__steer'),
            items: [
                canViewTeamPage && {
                    id: NavPathOverrideEnum.TEAM,
                    name: t('page_title', { ns: 'team' }),
                    href: '/team',
                    icon: Users,
                },
                {
                    id: NavPathOverrideEnum.CUSTOMERS,
                    name: t('page_title__customers'),
                    href: '/customers',
                    icon: NavCustomer,
                },
            ],
        },
        {
            id: 'analyze',
            label: t('nav_group__analyze'),
            items: [
                {
                    id: NavPathOverrideEnum.EXPLORER,
                    name: t('page_title__explorer'),
                    href: '/explorer',
                    icon: NavChart,
                },
                canReadOrders && {
                    id: NavPathOverrideEnum.SALES_ORDERS,
                    name: t('page_title__sales_orders'),
                    href: '/orders',
                    icon: OrderIcon,
                },
                canReadQuotes && {
                    id: NavPathOverrideEnum.SALES_QUOTES,
                    name: t('page_title__sales_quotes'),
                    href: '/quotes',
                    icon: QuoteIcon,
                },
                canViewGlobalAnalytics && {
                    id: NavPathOverrideEnum.ACTIVITIES,
                    name: t('page_title__activities'),
                    href: '/activities',
                    icon: Activity,
                },
                enableCustomerEventPlanning && {
                    id: NavPathOverrideEnum.EVENTS,
                    name: t('page_title__events'),
                    href: '/events',
                    icon: Calendar,
                },
            ],
        },
    ];

    return rawGroups
        .map(
            (group): MenuGroup => ({
                id: group.id,
                label: group.label,
                items: group.items.filter((item): item is MenuItem => Boolean(item)),
            }),
        )
        .filter((group) => group.items.length > 0);
};
